import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { useFormSuccess } from "@/shared/hooks/useFormSuccess";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { ApiError } from "@/shared/lib/apiClient";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass, selectClass } from "../components/FormField";
import { provinceOptions } from "../data";
import {
  fetchCompanyEmployee,
  fetchCompanyHospitals,
  updateCompanyEmployee,
  type UpdateCompanyEmployeePayload,
} from "../api/companyApi";
import {
  fetchAdminEmployee,
  fetchAdminNetworkHospitals,
  updateAdminEmployee,
} from "../api/adminApi";
import { EmployeeNetworkHospitalsField } from "../components/EmployeeNetworkHospitalsField";
import { EmployeeAvatar } from "../components/EmployeeAvatar";
import { getAuthToken, getStoredAccountType } from "../auth";
import { FAMILY_RELATIONSHIP_OPTIONS } from "../lib/familyRelationships";
import { EMPLOYEE_POSITION_OPTIONS, normalizeEmployeePosition } from "../lib/employeePositions";
import { parseEmployeeRouteId } from "../lib/employeeRouteId";
import { cn } from "@/shared/lib/cn";

function normalizeIncomingRelationship(raw: string): string {
  const value = raw.trim().toLowerCase();
  if (
    value === "spouse" ||
    value === "conjoint" ||
    value === "époux" ||
    value === "epoux" ||
    value === "épouse" ||
    value === "epouse"
  ) {
    return "spouse";
  }
  if (value === "child" || value === "enfant" || value === "fils" || value === "fille") {
    return "child";
  }
  return FAMILY_RELATIONSHIP_OPTIONS.some((o) => o.value === value) ? value : "";
}

type DependentForm = {
  key: number;
  id?: number;
  name: string;
  relationship: string;
  gender: string;
  birthdate: string;
  status: "active" | "inactive";
  photo?: string | null;
  photoFile?: File | null;
};

export function EditEmployeePage() {
  const { id } = useParams();
  const employeeId = parseEmployeeRouteId(id);
  const navigate = useNavigate();
  const saveSuccess = useFormSuccess();
  const { showToast } = usePageActions();
  const isAdmin = getStoredAccountType() === "admin";

  const [loading, setLoading] = useState(true);
  const [employeeName, setEmployeeName] = useState("");
  const [matricule, setMatricule] = useState("");
  const [matriculeCompany, setMatriculeCompany] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [gender, setGender] = useState("");
  const [hospitalId, setHospitalId] = useState("");
  const [selectedHospitalIds, setSelectedHospitalIds] = useState<number[]>([]);
  const [primaryHospitalId, setPrimaryHospitalId] = useState<number | null>(null);
  const [position, setPosition] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [status, setStatus] = useState<"active" | "inactive">("active");
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [hospitals, setHospitals] = useState<Array<{ id: number; name: string }>>([]);
  const [dependents, setDependents] = useState<DependentForm[]>([]);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!getAuthToken() || Number.isNaN(employeeId) || employeeId <= 0) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const emp = isAdmin
          ? await fetchAdminEmployee(employeeId)
          : await fetchCompanyEmployee(employeeId);
        if (cancelled) return;
        setEmployeeName(emp.name ?? "");
        setMatricule(emp.system_id ?? "");
        setMatriculeCompany(emp.company_matricule ?? "");
        setBirthdate((emp.birthdate as string) ?? "");
        setGender((emp.gender as string) ?? "");
        setPhone((emp.phone as string) ?? "");
        setEmail((emp.email as string) ?? "");
        setCity((emp.city as string) ?? "");
        setProvince(emp.province ?? "");
        setPosition(normalizeEmployeePosition((emp.position as string) ?? ""));
        setStatus(emp.status === "inactive" ? "inactive" : "active");
        const cid = typeof emp.company_id === "number" ? emp.company_id : null;
        setCompanyId(cid);
        const ids =
          Array.isArray(emp.hospital_ids) && emp.hospital_ids.length
            ? emp.hospital_ids.map(Number)
            : emp.hospital_id
              ? [Number(emp.hospital_id)]
              : [];
        setSelectedHospitalIds(ids);
        setPrimaryHospitalId(
          emp.primary_hospital_id != null
            ? Number(emp.primary_hospital_id)
            : ids[0] ?? null,
        );
        setHospitalId(ids[0] ? String(ids[0]) : "");
        setPhotoUrl(emp.photo || null);
        setDependents(
          (emp.family_members ?? []).map((m, i) => ({
            key: i + 1,
            id: m.id,
            name: m.name,
            relationship: normalizeIncomingRelationship(m.relationship ?? ""),
            gender: m.gender ?? "",
            birthdate: m.birthdate ?? "",
            status: m.status === "inactive" ? "inactive" : "active",
            photo: "photo" in m ? ((m as { photo?: string | null }).photo ?? null) : null,
            photoFile: null,
          })),
        );

        if (isAdmin && cid) {
          const rows = await fetchAdminNetworkHospitals(cid);
          if (!cancelled) setHospitals(rows);
        } else if (!isAdmin) {
          const rows = await fetchCompanyHospitals();
          if (!cancelled) setHospitals(rows);
        }
      } catch (e) {
        showToast({
          message: e instanceof ApiError ? e.message : "Chargement impossible.",
          variant: "danger",
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [employeeId, isAdmin, showToast]);

  const handleSubmit = async () => {
    if (!employeeName.trim() || !matriculeCompany.trim()) {
      showToast({ message: "Nom et matricule entreprise obligatoires.", variant: "warning" });
      return;
    }
    setSaving(true);
    try {
      const payload: UpdateCompanyEmployeePayload = {
        employee_name: employeeName.trim(),
        matricule_company: matriculeCompany.trim(),
        matricule: matricule.trim() || undefined,
        birthdate: birthdate || undefined,
        gender: gender === "M" || gender === "F" ? gender : undefined,
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        city: city.trim() || undefined,
        province: province.trim() || undefined,
        position: position.trim() || undefined,
        hospital_id: primaryHospitalId ?? selectedHospitalIds[0] ?? undefined,
        hospital_ids: selectedHospitalIds,
        primary_hospital_id: primaryHospitalId ?? undefined,
        status,
        family_members: dependents
          .filter((d) => d.name.trim())
          .map((d) => ({
            id: d.id,
            name: d.name.trim(),
            relationship: d.relationship || undefined,
            birthdate: d.birthdate || undefined,
            gender: d.gender === "M" || d.gender === "F" ? d.gender : undefined,
            status: d.status,
          })),
      };
      if (isAdmin) {
        await updateAdminEmployee(
          employeeId,
          { ...payload, company_id: companyId ?? undefined },
          photoFile,
          dependents.filter((d) => d.name.trim()).map((d) => d.photoFile ?? null),
        );
      } else {
        await updateCompanyEmployee(
          employeeId,
          payload,
          photoFile,
          dependents.filter((d) => d.name.trim()).map((d) => d.photoFile ?? null),
        );
      }
      saveSuccess("/dashboard/employees", "Employé mis à jour.");
      navigate(isAdmin ? "/dashboard/employees" : `/dashboard/employees/${employeeId}`);
    } catch (e) {
      showToast({
        message: e instanceof ApiError ? e.message : "Échec de la mise à jour.",
        variant: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  if (Number.isNaN(employeeId) || employeeId <= 0) {
    return (
      <div className="theme-card space-y-3 p-8 text-center">
        <p className="text-sm text-danger">Identifiant employé invalide.</p>
        <Link to="/dashboard/employees" className="text-sm text-primary hover:underline">
          Retour à la liste
        </Link>
      </div>
    );
  }

  if (loading) {
    return <div className="theme-card p-8 text-center text-sm text-muted">Chargement…</div>;
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <Breadcrumbs
          homeHref={isAdmin ? "/dashboard/admin" : "/dashboard/enterprise"}
          items={[
            { label: "Employés", href: "/dashboard/employees" },
            { label: "Modifier" },
          ]}
        />
        <Link
          to={isAdmin ? "/dashboard/employees" : `/dashboard/employees/${employeeId}`}
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm"
        >
          <ArrowLeft size={15} />
          Retour
        </Link>
      </div>

      <div className="theme-card space-y-4 p-6">
        <div className="flex flex-wrap items-center gap-4">
          <EmployeeAvatar
            name={employeeName || "Employé"}
            photo={photoUrl}
            size={72}
            rounded="md"
          />
          <h1 className="text-xl font-semibold text-foreground">Modifier l&apos;employé</h1>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField label="Nom complet">
            <input className={inputClass} value={employeeName} onChange={(e) => setEmployeeName(e.target.value)} />
          </FormField>
          <FormField label="Matricule système">
            <input className={inputClass} value={matricule} onChange={(e) => setMatricule(e.target.value)} />
          </FormField>
          <FormField label="Matricule entreprise">
            <input className={inputClass} value={matriculeCompany} onChange={(e) => setMatriculeCompany(e.target.value)} />
          </FormField>
          <FormField label="Date de naissance">
            <input type="date" className={inputClass} value={birthdate} onChange={(e) => setBirthdate(e.target.value)} />
          </FormField>
          <FormField label="Sexe">
            <select className={selectClass} value={gender} onChange={(e) => setGender(e.target.value)}>
              <option value="">—</option>
              <option value="M">Masculin</option>
              <option value="F">Féminin</option>
            </select>
          </FormField>
          <FormField label="Statut">
            <select
              className={selectClass}
              value={status}
              onChange={(e) => setStatus(e.target.value as "active" | "inactive")}
            >
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>
          </FormField>
          <FormField label="Téléphone">
            <input className={inputClass} value={phone} onChange={(e) => setPhone(e.target.value)} />
          </FormField>
          <FormField label="Email">
            <input className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} />
          </FormField>
          <FormField label="Ville">
            <input className={inputClass} value={city} onChange={(e) => setCity(e.target.value)} />
          </FormField>
          <FormField label="Province">
            <select className={selectClass} value={province} onChange={(e) => setProvince(e.target.value)}>
              <option value="">—</option>
              {provinceOptions
                .filter((p) => p.value)
                .map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
            </select>
          </FormField>
          <FormField label="Poste">
            <select className={selectClass} value={position} onChange={(e) => setPosition(e.target.value)}>
              <option value="">— Sélectionner —</option>
              {EMPLOYEE_POSITION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
              {position &&
              !EMPLOYEE_POSITION_OPTIONS.some((o) => o.value === position) ? (
                <option value={position}>{position}</option>
              ) : null}
            </select>
          </FormField>
          <EmployeeNetworkHospitalsField
            hospitals={hospitals}
            selectedIds={selectedHospitalIds}
            primaryId={primaryHospitalId}
            onChange={({ selectedIds, primaryId }) => {
              setSelectedHospitalIds(selectedIds);
              setPrimaryHospitalId(primaryId);
              setHospitalId(primaryId ? String(primaryId) : "");
            }}
          />
          <FormField label="Photo">
            <input type="file" accept="image/*" onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)} />
          </FormField>
        </div>

        <div className="border-t border-border pt-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold">Ayants droit</h2>
            <button
              type="button"
              className="inline-flex items-center gap-1 text-sm text-primary"
              onClick={() =>
                setDependents((prev) => [
                  ...prev,
                  {
                    key: Date.now(),
                    name: "",
                    relationship: "",
                    gender: "",
                    birthdate: "",
                    status: "active",
                    photoFile: null,
                  },
                ])
              }
            >
              <Plus size={14} /> Ajouter
            </button>
          </div>
          {dependents.map((d) => (
            <div
              key={d.key}
              className={cn(
                "mb-3 grid grid-cols-1 gap-2 md:grid-cols-8 md:items-center",
                d.status === "inactive" && "opacity-70",
              )}
            >
              <EmployeeAvatar
                name={d.name || "Ayant droit"}
                photo={d.photo}
                size={40}
                rounded="md"
              />
              <input
                className={inputClass}
                placeholder="Nom"
                value={d.name}
                onChange={(e) =>
                  setDependents((prev) => prev.map((x) => (x.key === d.key ? { ...x, name: e.target.value } : x)))
                }
              />
              <select
                className={selectClass}
                value={d.relationship}
                onChange={(e) =>
                  setDependents((prev) =>
                    prev.map((x) => (x.key === d.key ? { ...x, relationship: e.target.value } : x)),
                  )
                }
              >
                <option value="">Lien</option>
                {FAMILY_RELATIONSHIP_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <select
                className={selectClass}
                value={d.gender}
                onChange={(e) =>
                  setDependents((prev) => prev.map((x) => (x.key === d.key ? { ...x, gender: e.target.value } : x)))
                }
              >
                <option value="">Sexe</option>
                <option value="M">M</option>
                <option value="F">F</option>
              </select>
              <input
                type="date"
                className={inputClass}
                value={d.birthdate}
                onChange={(e) =>
                  setDependents((prev) =>
                    prev.map((x) => (x.key === d.key ? { ...x, birthdate: e.target.value } : x)),
                  )
                }
              />
              <select
                className={selectClass}
                value={d.status}
                onChange={(e) =>
                  setDependents((prev) =>
                    prev.map((x) =>
                      x.key === d.key
                        ? { ...x, status: e.target.value as "active" | "inactive" }
                        : x,
                    ),
                  )
                }
                title="Statut de l'ayant droit"
              >
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
              </select>
              <input
                type="file"
                accept="image/*"
                className="text-sm"
                onChange={(e) =>
                  setDependents((prev) =>
                    prev.map((x) =>
                      x.key === d.key ? { ...x, photoFile: e.target.files?.[0] ?? null } : x,
                    ),
                  )
                }
              />
              <button
                type="button"
                className="text-danger"
                onClick={() => setDependents((prev) => prev.filter((x) => x.key !== d.key))}
                title="Supprimer"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          disabled={saving}
          onClick={() => void handleSubmit()}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
        >
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>
    </>
  );
}
